<template>
  <v-form ref="form" v-model="validModal" lazy-validation>
    <!-- Token procedure -->
    <v-row no-gutters>
      <v-col cols="12" class="text-subtitle-1 text-lg-subtitle-1 font-weight-bold mb-3">
        {{ titles.sub.subOne }}
        <tooltip-component
          text="A token procedure is connected to a specific CA and only registered devices can enroll for certificates from the CA."
        />
      </v-col>
      <div class="text-style-req">
        <v-select
          class="text-body-2 text-lg-body-2 font-weight-regular"
          hide-details
          :menu-props="{ bottom: true, offsetY: true }"
          :items="procedureObject.proceduresWithProtocol"
          item-text="name"
          item-value="procid"
          v-model="procedureObject.registerProcModel"
          return-object
          outlined
          data-cy="Registrations-add-device-procedure"
          dense
        >
          <v-icon slot="append-outer" class="mb-4" x-small color="#EA0028">
            {{ icons.required }}
          </v-icon>
          <template v-slot:label>
            <span class="text-body-2 text-lg-body-2">
              {{ procedureObject.procedureLabel }}
            </span>
          </template>
          <template v-slot:item="{ index, item }">
            <div v-if="$vuetify.breakpoint.smAndDown" style="width: 12rem" class="mb-1">
              {{ item.name }}
            </div>
            <div v-else style="width: 16rem" class="mb-1">
              {{ item.name }}
            </div>
          </template>
        </v-select>
      </div>
    </v-row>

    <!-- Dynamic content -->
    <v-row no-gutters>
      <v-col
        cols="12"
        class="text-subtitle-1 text-lg-subtitle-1 font-weight-bold mt-6 mb-3"
      >
        {{ titles.sub.subTwo }}
        <tooltip-component
          text="Register a device using subject distinguished name data and password. The registered data will be used to match the content of a certificate enrollment request. 
        The API type decides which data can be registered."
        />
      </v-col>
      <div class="text-style" v-if="protocolObject.protocolModel === 'EST'">
        <v-select
          class="text-body-2 text-lg-body-2 font-weight-regular"
          v-model="registerCreationElements.EST.authOptions.authModel"
          outlined
          :menu-props="{ bottom: true, offsetY: true }"
          :items="registerCreationElements.EST.authOptions.authMethods"
          dense
          item-text="name"
          item-value="id"
        >
          <template v-slot:label>
            <span class="text-body-2 text-lg-body-2">
              {{ registerCreationElements.EST.authOptions.authLabel }}
            </span>
          </template>
          <template v-slot:item="{ index, item }">
            <div v-if="$vuetify.breakpoint.smAndDown" style="width: 10rem">
              {{ item.name }}
            </div>
            <div v-else>{{ item.name }}</div>
          </template>
        </v-select>
      </div>
      <v-col cols="12"></v-col>
      <div class="text-style-req" v-if="!protocolObject.protocolExist">
        <v-text-field dense outlined type="text">
          <v-icon slot="append-outer" class="mb-4" x-small color="#EA0028">
            {{ icons.required }}
          </v-icon>
          <template v-slot:label>
            <span class="text-body-2 text-lg-body-2">
              {{ protocolObject.protocolLabel }}
            </span>
          </template>
        </v-text-field>
      </div>
      <div v-if="protocolObject.protocolExist">
        <div
          v-for="(textField, index) in registerCreationElements[
            protocolObject.protocolModel
          ].textFields"
          :key="index"
          :class="textField.required ? 'text-style-req' : 'text-style'"
        >
          <v-text-field
            outlined
            dense
            :rules="textField.rules"
            :type="textField.type"
            v-model="textField.model"
          >
            <v-icon
              v-if="textField.required"
              slot="append-outer"
              class="mb-4"
              x-small
              color="#EA0028"
            >
              {{ icons.required }}
            </v-icon>
            <template v-slot:label>
              <span class="text-body-2 text-lg-body-2">
                {{ textField.label }}
              </span>
            </template>
          </v-text-field>
        </div>
      </div>
      <v-col cols="12"></v-col>
      <div
        v-if="
          protocolObject.protocolExist &
          (protocolObject.protocolModel === 'EST') &
          (registerCreationElements.EST.authOptions.authModel === 1)
        "
      >
        <div
          v-for="(textField, index) in registerCreationElements.EST.certAuth"
          :key="index"
          :class="textField.required ? 'text-style-req' : 'text-style'"
        >
          <v-text-field
            outlined
            dense
            :rules="textField.rules"
            :type="textField.type"
            v-model="textField.model"
          >
            <v-icon
              v-if="textField.required"
              slot="append-outer"
              class="mb-4"
              x-small
              color="#EA0028"
            >
              {{ icons.required }}
            </v-icon>
            <template v-slot:label>
              <span class="text-body-2 text-lg-body-2">
                {{ textField.label }}
              </span>
            </template>
          </v-text-field>
        </div>
      </div>

      <div
        v-if="
          protocolObject.protocolExist &
          (protocolObject.protocolModel === 'EST') &
          (registerCreationElements.EST.authOptions.authModel === 2)
        "
      >
        <div
          v-for="(textField, index) in registerCreationElements.EST.httpAuth"
          :key="index"
          :class="textField.required ? 'text-style-req' : 'text-style'"
        >
          <v-text-field
            outlined
            dense
            :rules="textField.rules"
            :type="textField.type"
            v-model="textField.model"
          >
            <v-icon
              v-if="textField.required"
              slot="append-outer"
              class="mb-4"
              x-small
              color="#EA0028"
            >
              {{ icons.required }}
            </v-icon>
            <template v-slot:label>
              <span class="text-body-2 text-lg-body-2">
                {{ textField.label }}
              </span>
            </template>
          </v-text-field>
        </div>
      </div>

      <div v-if="protocolObject.protocolModel === 'SCEP'">
        <v-row no-gutters>
          <v-col cols="11" md="4" lg="4" class="mr-md-4">
            <v-select
              class="text-body-2 text-lg-body-2 font-weight-regular text-style"
              outlined
              v-model="registerCreationElements.SCEP.ipOptions.versionModel"
              :items="registerCreationElements.SCEP.ipOptions.ipVersions"
              :menu-props="{ bottom: true, offsetY: true }"
              dense
              item-text="name"
            >
              <template v-slot:label>
                <span class="text-body-2 text-lg-body-2">
                  {{ registerCreationElements.SCEP.ipOptions.ipVersionLabel }}
                </span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="11" md="6" lg="6">
            <v-text-field
              outlined
              dense
              v-model="registerCreationElements.SCEP.ipOptions.ipModel"
              :rules="computedRules"
              :error-messages="registerCreationElements.SCEP.ipOptions.textFieldErr"
              class="text-style text-body-2 text-lg-body-2"
            >
              <template v-slot:label>
                <span class="text-body-2 text-lg-body-2">
                  {{ registerCreationElements.SCEP.ipOptions.ipAdressLabel }}
                </span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </div>

      <v-row no-gutters v-if="protocolObject.protocolModel === 'ACME'">
        <div class="text-style">
          <div class="text-body-2 text-lg-body-2">
            hmackey for registered entity: <tooltip-component text="" />
          </div>
          <v-text-field
            outlined
            dense
            v-model="registerCreationElements.ACME.hmacKeyOptions.hmacKeyModel"
            disabled
          >
            <template slot="append">
              <v-btn icon @click="copyMaterial()" v-if="!textCopiedContainer">
                <v-icon small>
                  {{ icons.copy }}
                </v-icon>
              </v-btn>
              <span v-else class="caption grey--text text--darken-2 text-none">
                Key copied
              </span>
            </template>
            <template v-slot:label>
              <span class="text-body-2 text-lg-body-2">
                {{ registerCreationElements.ACME.hmacKeyOptions.hmacKeyLabel }}
              </span>
            </template>
          </v-text-field>
        </div>
      </v-row>
    </v-row>

    <!-- Validity -->
    <v-row no-gutters>
      <v-col cols="12" class="text-subtitle-1 text-lg-subtitle-1 font-weight-bold mb-2">
        {{ titles.sub.subThree }}
        <tooltip-component
          text="Specifies how many days the registration state will be open until automatically closed.
         As long as the registration's state is 'Open' the device can enroll for a certificate."
        />
      </v-col>
      <v-col cols="6" style="display: flex">
        <div class="mt-2 text-body-2 text-lg-body-2 font-weight-regular">
          {{ registerCreationElements.validity.label }}
        </div>
        <v-switch
          inset
          color="#006CA1"
          class="ml-2 mt-0"
          dense
          v-model="registerCreationElements.validity.status"
          true-value="Open"
          false-value="Closed"
        >
          <template v-slot:label>
            <span class="text-body-2 text-lg-body-2">{{
              registerCreationElements.validity.status
            }}</span>
          </template>
        </v-switch>
      </v-col>
    </v-row>

    <!-- Validity input -->
    <div
      v-if="registerCreationElements.validity.status === 'Open'"
      class="text-body-2 text-lg-body-2"
    >
      <global-radio-group
        txt="Validity period"
        :radioGroup.sync="registerCreationElements.validity.validityType"
        :labelOne="registerCreationElements.validity.optionOneLabel"
        :labelTwo="registerCreationElements.validity.optionTwoLabel"
      ></global-radio-group>
      <v-row no-gutters v-if="registerCreationElements.validity.validityType === '1'">
        <v-col cols="7" sm="4" md="3" lg="3" xl="2">
          <v-text-field
            class=""
            background-color="white"
            outlined
            dense
            @keydown="handleKeyDown"
            v-model="registerCreationElements.validity.validity_duration"
            :rules="registerCreationElements.validity.validity_rule"
            type="number"
            min="1"
          >
            <template v-slot:label>
              <span class="text-body-2 text-lg-body-2">
                {{ registerCreationElements.validity.durationLabel }}
              </span>
            </template>
          </v-text-field>
        </v-col>
        <v-col class="pt-3 ml-2 text-body-2 text-lg-body-2" cols="4">
          days
          <v-icon class="" x-small color="#EA0028">
            {{ icons.required }}
          </v-icon>
        </v-col>
      </v-row>
    </div>

    <!-- Btns -->
    <v-row no-gutters>
      <v-col cols="4" md="1" lg="1"></v-col>

      <v-col cols="12" md="11" lg="11" class="text-right">
        <div
          class="text-md-center text-lg-center orange--text text--darken-2 mb-2"
          v-if="buttonLoader"
        >
          <v-icon class="orange--text text--darken-2">fa-light fa-file-signature</v-icon>
          Please sign the request using the Personal desktop app.
        </div>
        <v-btn
          v-if="!buttonLoader || !done"
          color="#006CA1"
          outlined
          class="mr-2 mr-md-4 text-none text-subtitle-2 text-lg-subtitle-1"
          @click="cancelCancel()"
          >Cancel</v-btn
        >
        <v-btn
          v-if="!done"
          @click="createRegistration()"
          :loading="buttonLoader"
          :disabled="procedureObject.registerProcModel === '' ? true : false"
          data-cy="Registrations-add-device-submit"
          class="mr-2 mr-md-4 text-none text-subtitle-2 text-lg-subtitle-1"
          style="background-color: #006ca1; color: #ffffff"
          >Register
        </v-btn>
        <v-btn
          class="text-none text-subtitle-2 text-subtitle-1"
          @click="clearClicked()"
          style="background-color: #54797a; color: #ffffff"
          v-if="done"
        >
          <v-icon small class="mr-2 text-none text-subtitle-1">
            {{ icons.done }}
          </v-icon>
          Done
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>
<script>
import TooltipComponent from "@/components/global/tooltip-component.vue";
import globalRadioGroup from "@/components/global/global-radio-group.vue";

export default {
  props: [
    "valid",
    "procedureObject",
    "protocolObject",
    "registerCreationElements",
    "buttonLoader",
    "done",
    "computedRules",
    "textCopiedContainer",
    "titles",
    "icons",
  ],
  components: {
    TooltipComponent,
    globalRadioGroup,
  },
  computed: {
    validModal: {
      get: function () {
        return this.valid;
      },
      set: function (newVal) {
        this.$emit("update:valid", newVal);
      },
    },
  },
  methods: {
    createRegistration() {
      this.$emit("createRegistration", this.$refs.form.validate());
    },
    cancelCancel() {
      this.$emit("cancelClicked", true);
    },
    clearClicked() {
      this.$emit("clearClicked");
    },
    copyMaterial() {
      this.$emit("copyMaterial");
    },
    handleKeyDown(event) {
      // Allow backspace, digits, and arrow keys (up and down)
      if (
        event.keyCode === 8 || // Backspace
        (event.keyCode >= 37 && event.keyCode <= 40) || // Arrow keys
        event.key.match(/^\d$/) // Digits
      ) {
        // Continue with the default behavior
      } else {
        // Prevent the input of non-digit characters and other keys
        event.preventDefault();
      }
    },
  },
};
</script>
<style scoped lang="scss">
.text-style {
  width: 14.5rem;
}
.text-style-req {
  width: 16rem;
}
@media only screen and (min-width: 600px) {
  .text-style {
    width: 19.5rem;
  }
  .text-style-req {
    width: 21rem;
  }
}

@media only screen and (min-width: 800px) {
  .text-style {
    width: 20rem;
  }
}
</style>
